import { ApiError } from "@/lib/api";
import { usePortalSessionStore } from "./store/portal-session-store";
import type {
  AuthStep,
  OnboardingProfile,
  PortalBranding,
  PortalClientSummary,
  PortalDocument,
} from "./types";

const API_BASE_URL = process.env.BUN_PUBLIC_API_URL ?? "http://localhost:8000/api/v1";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
  meta?: unknown;
}

interface PortalFetchOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
  form?: FormData;
  /** Attach the portal *session* token (default true). */
  auth?: boolean;
  /** Attach an explicit onboarding-session token instead of the portal token. */
  onboardingToken?: string;
}

function pickError(data: unknown, fallback: string): string {
  if (!data || typeof data !== "object") return fallback;
  const rec = data as Record<string, unknown>;
  if (typeof rec.message === "string" && rec.message) return rec.message;
  if (typeof rec.detail === "string") return rec.detail;
  const errors = rec.errors;
  if (errors && typeof errors === "object") {
    for (const v of Object.values(errors as Record<string, unknown>)) {
      if (typeof v === "string") return v;
      if (Array.isArray(v) && typeof v[0] === "string") return v[0];
    }
  }
  return fallback;
}

/** Portal fetch: attaches the portal (or onboarding) token, unwraps the API
 * envelope, and on a 401 clears the portal session so the guard redirects. */
async function portalRaw(path: string, options: PortalFetchOptions): Promise<Response> {
  const { json, form, auth = true, onboardingToken, headers, ...rest } = options;
  const finalHeaders = new Headers(headers);
  if (json !== undefined) finalHeaders.set("Content-Type", "application/json");
  if (onboardingToken) {
    finalHeaders.set("Authorization", `Bearer ${onboardingToken}`);
  } else if (auth) {
    const token = usePortalSessionStore.getState().sessionToken;
    if (token) finalHeaders.set("Authorization", `Bearer ${token}`);
  }
  return fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : form,
  });
}

export async function portalFetch<T>(path: string, options: PortalFetchOptions = {}): Promise<T> {
  const res = await portalRaw(path, options);
  if (res.status === 401 && (options.auth ?? true) && !options.onboardingToken) {
    usePortalSessionStore.getState().clearSession();
  }
  if (res.status === 204 || res.status === 205) return undefined as T;
  const contentType = res.headers.get("Content-Type") ?? "";
  const body = contentType.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    throw new ApiError(res.status, pickError(body, `Request failed (${res.status})`), body);
  }
  return (body as Envelope<T>).data;
}

// --- mappers -----------------------------------------------------------------
function mapBranding(b: Record<string, unknown>): PortalBranding {
  return {
    firmName: (b.firm_name as string) ?? "",
    portalName: (b.portal_name as string) ?? "",
    showName: Boolean(b.show_name),
    footerLeft: (b.footer_left as string) ?? "",
    footerRight: (b.footer_right as string) ?? "",
    logoUrl: (b.logo_url as string) ?? "",
    phone: (b.phone as string) ?? "",
    email: (b.email as string) ?? "",
  };
}

function mapClient(c: Record<string, unknown>): PortalClientSummary {
  return {
    accessId: c.access_id as string,
    clientId: c.client_id as string,
    name: c.name as string,
    party: c.party as "T" | "S",
    clientType: c.client_type as "INDIVIDUAL" | "BUSINESS",
    showDocuments: Boolean(c.show_documents),
    showFinancials: Boolean(c.show_financials),
  };
}

function mapAuthStep(d: Record<string, unknown>): AuthStep {
  return {
    stage: d.stage as AuthStep["stage"],
    token: d.token as string | undefined,
    clients: Array.isArray(d.clients)
      ? (d.clients as Record<string, unknown>[]).map(mapClient)
      : undefined,
    client: d.client ? mapClient(d.client as Record<string, unknown>) : undefined,
    branding: d.branding ? mapBranding(d.branding as Record<string, unknown>) : undefined,
  };
}

function mapDocument(d: Record<string, unknown>): PortalDocument {
  return {
    id: d.id as string,
    name: d.name as string,
    contentType: (d.content_type as string) ?? "",
    sizeBytes: (d.size_bytes as number) ?? 0,
    version: (d.version as number) ?? 1,
    createdAt: d.created_at as string,
  };
}

// --- auth --------------------------------------------------------------------
export async function resolvePortal(
  portalName: string,
): Promise<{ enabled: boolean; branding?: PortalBranding }> {
  const data = await portalFetch<Record<string, unknown>>(`/portal/${portalName}/`, {
    auth: false,
  });
  return {
    enabled: Boolean(data.enabled),
    branding: data.branding ? mapBranding(data.branding as Record<string, unknown>) : undefined,
  };
}

export async function portalSignup(portalName: string, body: { email: string; tin: string }) {
  return mapAuthStep(
    await portalFetch(`/portal/${portalName}/signup/`, { method: "POST", json: body, auth: false }),
  );
}

export async function portalLogin(portalName: string, body: { email: string; password: string }) {
  return mapAuthStep(
    await portalFetch(`/portal/${portalName}/login/`, { method: "POST", json: body, auth: false }),
  );
}

export async function portalForgot(portalName: string, body: { email: string; tin: string }) {
  return mapAuthStep(
    await portalFetch(`/portal/${portalName}/forgot/`, { method: "POST", json: body, auth: false }),
  );
}

export async function portalVerify2fa(body: { token: string; code: string }) {
  return mapAuthStep(
    await portalFetch(`/portal/verify-2fa/`, { method: "POST", json: body, auth: false }),
  );
}

export async function portalResend2fa(token: string) {
  return portalFetch(`/portal/resend-2fa/`, { method: "POST", json: { token }, auth: false });
}

export async function portalSetPassword(body: { token: string; password: string }) {
  return mapAuthStep(
    await portalFetch(`/portal/set-password/`, { method: "POST", json: body, auth: false }),
  );
}

export async function portalSelectClients(token: string): Promise<PortalClientSummary[]> {
  const data = await portalFetch<{ clients: Record<string, unknown>[] }>(
    `/portal/clients/?token=${encodeURIComponent(token)}`,
    { auth: false },
  );
  return data.clients.map(mapClient);
}

export async function portalSelectClient(body: { token: string; access_id: string }) {
  return mapAuthStep(
    await portalFetch(`/portal/select-client/`, { method: "POST", json: body, auth: false }),
  );
}

export async function portalChangePassword(body: {
  old_password: string;
  password1: string;
  password2: string;
}) {
  return portalFetch(`/portal/change-password/`, { method: "POST", json: body });
}

export async function portalLogout() {
  return portalFetch(`/portal/logout/`, { method: "POST" });
}

export async function portalMe(): Promise<{ client: PortalClientSummary; branding: PortalBranding }> {
  const data = await portalFetch<{ client: Record<string, unknown>; branding: Record<string, unknown> }>(
    `/portal/me/`,
  );
  return { client: mapClient(data.client), branding: mapBranding(data.branding) };
}

// --- onboarding --------------------------------------------------------------
export async function onboardingStart(
  token: string,
): Promise<OnboardingProfile & { state: "ok" | "completed" | "invalid" }> {
  const data = await portalFetch<Record<string, unknown>>(
    `/portal/onboarding/${token}/start/`,
    { method: "POST", auth: false },
  );
  return {
    state: data.state as "ok" | "completed" | "invalid",
    clientType: data.client_type as OnboardingProfile["clientType"],
    profile: (data.profile as Record<string, unknown>) ?? {},
    branding: mapBranding((data.branding as Record<string, unknown>) ?? {}),
    token: data.token as string | undefined,
  };
}

export async function onboardingMe(
  onboardingToken: string,
): Promise<OnboardingProfile> {
  const data = await portalFetch<Record<string, unknown>>(`/portal/onboarding/me/`, {
    onboardingToken,
  });
  return {
    clientType: data.client_type as OnboardingProfile["clientType"],
    profile: (data.profile as Record<string, unknown>) ?? {},
    branding: mapBranding((data.branding as Record<string, unknown>) ?? {}),
  };
}

export async function onboardingSaveField(
  onboardingToken: string,
  patch: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return portalFetch(`/portal/onboarding/field/`, {
    method: "PATCH",
    json: patch,
    onboardingToken,
  });
}

export async function onboardingCounties(
  onboardingToken: string,
  state: string,
): Promise<string[]> {
  const data = await portalFetch<{ counties: string[] }>(
    `/portal/onboarding/counties/?state=${encodeURIComponent(state)}`,
    { onboardingToken },
  );
  return data.counties;
}

export async function onboardingSubmit(onboardingToken: string) {
  return portalFetch(`/portal/onboarding/submit/`, { method: "POST", onboardingToken });
}

// --- documents ---------------------------------------------------------------
export async function listPublishedDocuments(): Promise<PortalDocument[]> {
  const data = await portalFetch<Record<string, unknown>[]>(`/portal/documents/`);
  return data.map(mapDocument);
}

export async function downloadPortalDocument(id: string): Promise<Blob> {
  const token = usePortalSessionStore.getState().sessionToken;
  const res = await fetch(`${API_BASE_URL}/portal/documents/${id}/download/`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new ApiError(res.status, `Download failed (${res.status})`, null);
  return res.blob();
}

export async function stageUpload(file: File): Promise<PortalDocument> {
  const form = new FormData();
  form.append("file", file);
  return mapDocument(await portalFetch(`/portal/documents/upload/`, { method: "POST", form }));
}

export async function listStagedUploads(): Promise<PortalDocument[]> {
  const data = await portalFetch<Record<string, unknown>[]>(`/portal/documents/uploads/`);
  return data.map(mapDocument);
}

export async function renameStagedUpload(id: string, name: string): Promise<PortalDocument> {
  return mapDocument(
    await portalFetch(`/portal/documents/uploads/${id}/`, { method: "PATCH", json: { name } }),
  );
}

export async function deleteStagedUpload(id: string): Promise<void> {
  await portalFetch(`/portal/documents/uploads/${id}/`, { method: "DELETE" });
}

export async function sendUploads(): Promise<{ sent: number }> {
  return portalFetch(`/portal/documents/send-uploads/`, { method: "POST" });
}

// --- profile (read-only; the client's own identity) --------------------------
export interface PortalProfile {
  name: string;
  clientType: "INDIVIDUAL" | "BUSINESS";
  email: string;
  country: string;
  taxIdKind: string;
  taxIdLast4: string;
}

export async function getProfile(): Promise<PortalProfile> {
  const d = await portalFetch<Record<string, unknown>>(`/portal/profile/`);
  return {
    name: d.name as string,
    clientType: d.client_type as PortalProfile["clientType"],
    email: (d.email as string) ?? "",
    country: (d.country as string) ?? "",
    taxIdKind: (d.tax_id_kind as string) ?? "",
    taxIdLast4: (d.tax_id_last4 as string) ?? "",
  };
}

// --- notices (IRS notices logged against the client, read-only) --------------
export interface PortalNotice {
  id: string;
  code: string;
  title: string;
  receivedOn: string | null;
  responseDeadline: string | null;
  status: "OPEN" | "IN_PROGRESS" | "RESOLVED";
  taxYear: string;
}

export async function listNotices(): Promise<PortalNotice[]> {
  const data = await portalFetch<Record<string, unknown>[]>(`/portal/notices/`);
  return data.map((n) => ({
    id: n.id as string,
    code: n.code as string,
    title: n.title as string,
    receivedOn: (n.received_on as string) ?? null,
    responseDeadline: (n.response_deadline as string) ?? null,
    status: n.status as PortalNotice["status"],
    taxYear: (n.tax_year as string) ?? "",
  }));
}

// --- onboarding Proforma Invoice (client approve/reject) ---------------------
// Distinct from the billing "Proposals" above: this is the engagement PI the
// firm sends during onboarding for the client to approve before work starts.
export interface PortalProformaHistoryItem {
  /** 1-based order the proposal was sent to the client ("Proposal N"). */
  proposalNumber: number;
  status: string;
  /** Client-facing label (Accepted / Declined / Awaiting your response). */
  label: string;
  decidedAt: string;
}

export interface PortalProformaState {
  hasProforma: boolean;
  name: string;
  stage: string;
  canDecide: boolean;
  rejectionReason: string;
  /** Every PI the client was sent, most-recent first (accepted/declined history). */
  history: PortalProformaHistoryItem[];
}

function mapProformaState(d: Record<string, unknown>): PortalProformaState {
  const history = Array.isArray(d.history) ? (d.history as Record<string, unknown>[]) : [];
  return {
    hasProforma: Boolean(d.has_proforma),
    name: (d.name as string) ?? "",
    stage: (d.stage as string) ?? "",
    canDecide: Boolean(d.can_decide),
    rejectionReason: (d.rejection_reason as string) ?? "",
    history: history.map((h) => ({
      proposalNumber: (h.proposal_number as number) ?? 0,
      status: (h.status as string) ?? "",
      label: (h.label as string) ?? "",
      decidedAt: (h.decided_at as string) ?? "",
    })),
  };
}

export async function getOnboardingProforma(): Promise<PortalProformaState> {
  return mapProformaState(
    await portalFetch<Record<string, unknown>>(`/portal/onboarding-proforma/`),
  );
}

export async function decideOnboardingProforma(
  decision: "accept" | "reject",
  note = "",
): Promise<PortalProformaState> {
  return mapProformaState(
    await portalFetch<Record<string, unknown>>(`/portal/onboarding-proforma/decision/`, {
      method: "POST",
      json: { decision, note },
    }),
  );
}

export async function downloadOnboardingProforma(): Promise<Blob> {
  const token = usePortalSessionStore.getState().sessionToken;
  const res = await fetch(`${API_BASE_URL}/portal/onboarding-proforma/download/`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new ApiError(res.status, `Download failed (${res.status})`, null);
  return res.blob();
}

// --- proforma proposals (approved estimates the firm shared) -----------------
export interface PortalProforma {
  id: string;
  invoiceNumber: string;
  invoiceDate: string | null;
  noteToClient: string;
  totalCents: number;
}

export async function listProformas(): Promise<PortalProforma[]> {
  const data = await portalFetch<Record<string, unknown>[]>(`/portal/proformas/`);
  return data.map((p) => ({
    id: p.id as string,
    invoiceNumber: p.invoice_number as string,
    invoiceDate: (p.invoice_date as string) ?? null,
    noteToClient: (p.note_to_client as string) ?? "",
    totalCents: (p.total_cents as number) ?? 0,
  }));
}

export async function downloadPortalProforma(id: string): Promise<Blob> {
  const token = usePortalSessionStore.getState().sessionToken;
  const res = await fetch(`${API_BASE_URL}/portal/proformas/${id}/download/`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new ApiError(res.status, `Download failed (${res.status})`, null);
  return res.blob();
}

// --- requested documents (what the firm asked this client to provide) --------
export interface PortalRequiredDocument {
  id: string;
  name: string;
  notes: string;
  status: "PENDING" | "RECEIVED" | "WAIVED";
  dueDate: string | null;
  receivedAt: string | null;
}

function mapRequiredDoc(d: Record<string, unknown>): PortalRequiredDocument {
  return {
    id: d.id as string,
    name: d.name as string,
    notes: (d.notes as string) ?? "",
    status: d.status as PortalRequiredDocument["status"],
    dueDate: (d.due_date as string) ?? null,
    receivedAt: (d.received_at as string) ?? null,
  };
}

export async function listRequiredDocuments(): Promise<PortalRequiredDocument[]> {
  const data = await portalFetch<Record<string, unknown>[]>(`/portal/required-documents/`);
  return data.map(mapRequiredDoc);
}

/** Upload a file fulfilling one requested document — sent straight to the firm
 * and the request is marked received. */
export async function uploadRequiredDocument(
  id: string,
  file: File,
): Promise<PortalRequiredDocument> {
  const form = new FormData();
  form.append("file", file);
  return mapRequiredDoc(
    await portalFetch(`/portal/required-documents/${id}/upload/`, { method: "POST", form }),
  );
}

// --- limited-field 433 worksheet --------------------------------------------
// Worksheet + line-item shapes are the Tier 2.2 serializers (snake_case, money
// in cents), reused verbatim by the portal financials components.
export function getPortalWorksheet(): Promise<Record<string, unknown>> {
  return portalFetch(`/portal/worksheet/`);
}

export function patchPortalWorksheet(patch: Record<string, unknown>): Promise<Record<string, unknown>> {
  return portalFetch(`/portal/worksheet/`, { method: "PATCH", json: patch });
}

export function getPortalIncomeExpense(): Promise<Record<string, unknown>> {
  return portalFetch(`/portal/worksheet/incexp/`);
}

export function patchPortalIncomeExpense(patch: Record<string, unknown>): Promise<Record<string, unknown>> {
  return portalFetch(`/portal/worksheet/incexp/`, { method: "PATCH", json: patch });
}

export function finishFinancials(): Promise<{ detail: string }> {
  return portalFetch(`/portal/worksheet/finish/`, { method: "POST" });
}

export interface PortalCollectionApi<T = Record<string, unknown>> {
  list: (query?: string) => Promise<T[]>;
  create: (payload: Record<string, unknown>) => Promise<T>;
  update: (id: string, payload: Record<string, unknown>) => Promise<T>;
  remove: (id: string) => Promise<void>;
}

function collection<T = Record<string, unknown>>(segment: string): PortalCollectionApi<T> {
  const base = `/portal/worksheet/${segment}/`;
  return {
    list: (query) => portalFetch<T[]>(query ? `${base}?${query}` : base),
    create: (payload) => portalFetch<T>(base, { method: "POST", json: payload }),
    update: (id, payload) => portalFetch<T>(`${base}${id}/`, { method: "PATCH", json: payload }),
    remove: (id) => portalFetch<void>(`${base}${id}/`, { method: "DELETE" }),
  };
}

export const portalOtherBusinesses = collection("other-businesses");
export const portalProperties = collection("properties");
export const portalVehicles = collection("vehicles");
export const portalOtherAssets = collection("assets");
export const portalAccounts = collection("accounts");
export const portalInsurance = collection("insurance");
export const portalTransfers = collection("transfers");
export const portalLawsuits = collection("lawsuits");
export const portalDependents = collection("dependents");
export const portalEmployers = collection("employers");

export function portalPaystubs(employerId: string): PortalCollectionApi {
  const base = `/portal/worksheet/employers/${employerId}/paystubs/`;
  return {
    list: () => portalFetch(base),
    create: (payload) => portalFetch(base, { method: "POST", json: payload }),
    update: (id, payload) => portalFetch(`${base}${id}/`, { method: "PATCH", json: payload }),
    remove: (id) => portalFetch(`${base}${id}/`, { method: "DELETE" }),
  };
}

// --- business worksheet set (limited-field 433-B) ---------------------------
export const portalBusinesses = collection("businesses");

/** A per-business sub-collection (personnel, accounts, assets, …) scoped to one
 * business id, mirroring `portalPaystubs`. */
export function portalBusinessChild(
  businessId: string,
  segment: string,
): PortalCollectionApi {
  const base = `/portal/worksheet/businesses/${businessId}/${segment}/`;
  return {
    list: (query) => portalFetch(query ? `${base}?${query}` : base),
    create: (payload) => portalFetch(base, { method: "POST", json: payload }),
    update: (id, payload) => portalFetch(`${base}${id}/`, { method: "PATCH", json: payload }),
    remove: (id) => portalFetch(`${base}${id}/`, { method: "DELETE" }),
  };
}
